'use client';

import { ChangeEvent, FC, useCallback, useMemo, useState } from 'react';
import { differenceInYears } from 'date-fns';
import { useRouter } from 'next/navigation';
import pluralize from 'pluralize';

import { Button, DatePicker, Icon, Input, useData } from '@/components';
import { useEcomStoreSelector, useForm, useUiStoreSelector } from '@/data';
import { AgeGateDocument } from '@/types';

import styles from './age.module.scss';
import {
  addStoreTerm,
  buildAgeAcceptance,
  GLOBAL_KEY,
  IProcessedAgeGateData,
  StoreTermsMap,
} from 'app/_components/AgeGate/utils';
import { CookiesNames } from 'constants/cookies';
import { useCookies } from '@/hooks';

export interface AgeGateData {
  ageGate: AgeGateDocument;
  crmModal: AgeGateDocument;
  header: AgeGateDocument;
}
interface AgeGateProps {
  data: IProcessedAgeGateData | null;
  redirectUrl?: string;
  /** Which terms were accepted: a per-location slice's slug, or `'global'`. */
  acceptanceKey?: string;
  autoAcceptStore?: string;
}

/** Return URL: prop wins, else the query param; must be a safe relative path. */
const getSafeReturnUrl = (redirectUrl?: string): string => {
  const returnUrl =
    redirectUrl ?? new URLSearchParams(window.location.search).get('returnUrl');
  const decodedReturnUrl = returnUrl ? decodeURIComponent(returnUrl) : '/';
  const isValidUrl =
    decodedReturnUrl.startsWith('/') && !decodedReturnUrl.startsWith('//');
  return isValidUrl ? decodedReturnUrl : '/';
};

const AgeGate: FC<AgeGateProps> = ({
  data,
  redirectUrl,
  acceptanceKey = 'global',
  autoAcceptStore,
}) => {
  const router = useRouter();
  const { setCookieObject, getCookieObject } = useCookies();
  const { treezStores } = useData();
  const [isLoading, setIsLoading] = useState(false);
  const { is_subscribed_to_crm, treez_store_name, setState } =
    useEcomStoreSelector(['is_subscribed_to_crm', 'treez_store_name']);
  const { setState: setUiState } = useUiStoreSelector([]);

  const dataAgeGate = data?.ageGate?.data;
  const crmModal = data?.crmModal?.data;

  // Use data directly from server without unnecessary memoization
  const type = dataAgeGate?.type || '';
  const buttonType = dataAgeGate?.button_type;
  const agreements = dataAgeGate?.agreements || [];
  const rememberMeDays = dataAgeGate?.remember_me;
  const allowedAge = dataAgeGate?.allowed_age || 0;

  // Drives decline: only a multi-store per-location gate falls back to the store selector.
  const isStoreScoped =
    (treezStores?.length ?? 0) > 1 && acceptanceKey !== GLOBAL_KEY;

  // Only memoize calculated text that depends on data that can change
  const rememberText = useMemo(() => {
    const daysLabel = `${pluralize('day', rememberMeDays || 0)}`;
    return `Remember me for ${rememberMeDays} ${daysLabel}.`;
  }, [rememberMeDays]);

  // Memoize initial agreements only because they are used in useForm
  const initialAgreements = useMemo(() => {
    const agreementsObj = {
      ...(rememberMeDays ? { remember_me: true } : {}),
    };

    if (agreements.length) {
      agreements.forEach((agreement: any, id) => {
        agreementsObj[`agreement_${id}`] = agreement.default_state;
      });
    }

    return agreementsObj;
  }, [rememberMeDays, agreements]);

  // Form states
  const [birthDate, setBirthDate] = useState<Date>();
  const [hasIncompleteFields, setHasIncompleteFields] = useState(false);
  const [isYounger, setIsYounger] = useState(false);
  const [agreementsAccepted, setAgreementsAccepted] = useState(true);

  const { inputs, genericChange } = useForm(initialAgreements);

  // Memoize error message
  const errorMessage = useMemo(() => {
    if (hasIncompleteFields) {
      return 'Please enter a valid date of birth.';
    }
    if (isYounger) {
      return `You must be at least ${allowedAge} years old.`;
    }
    if (!agreementsAccepted) {
      return 'You must agree to our terms before continuing.';
    }
    return null;
  }, [hasIncompleteFields, isYounger, agreementsAccepted, allowedAge]);

  // Callback to clear errors
  const clearErrorMessage = useCallback(() => {
    setTimeout(() => {
      setHasIncompleteFields(false);
      setIsYounger(false);
      setAgreementsAccepted(true);
    }, 4000);
  }, []);

  // Callback to verify agreements
  const areAgreementsAccepted = useCallback(() => {
    if (!inputs) return true;

    const accepted = Object.values(inputs).every(v => v);
    setAgreementsAccepted(accepted);

    if (!accepted) {
      clearErrorMessage();
      return false;
    }

    return true;
  }, [inputs, clearErrorMessage]);

  // Callback to handle submission
  const handleSubmit = useCallback(
    async (event: Event) => {
      event.preventDefault();

      if (!areAgreementsAccepted()) return;

      setIsLoading(true);

      const { state, cookie, cookieMaxAge } = buildAgeAcceptance({
        rememberMeDays,
        rememberChecked: !!inputs.remember_me,
      });

      // Local state for your app (don't confuse with server cookie)
      setState(state);

      setCookieObject(CookiesNames._TERMS_ACCEPTED, cookie, {
        maxAge: cookieMaxAge,
      });

      const storeKey =
        acceptanceKey !== GLOBAL_KEY ? acceptanceKey : autoAcceptStore;
      if (storeKey) {
        const current = getCookieObject<StoreTermsMap>(
          CookiesNames._STORE_TERMS,
        );
        setCookieObject(
          CookiesNames._STORE_TERMS,
          addStoreTerm(current, storeKey),
          { maxAge: cookieMaxAge },
        );
      }

      // SHOW CRM MODAL BEFORE REDIRECTING
      if (crmModal && is_subscribed_to_crm === null) {
        const modal = document.getElementById('age-gate-modal');
        if (modal) {
          modal.style.display = 'none';
        }
        setIsLoading(false);
        setState({ is_crm_modal_open: true });
      }

      // The "no store committed yet" effects reopen the selector while the pending store waits for
      // its gate. The choice is settled now, and the ui store survives the navigation back.
      setUiState({ is_store_location_open: false });

      // Redirect
      router.replace(getSafeReturnUrl(redirectUrl));
    },
    [
      areAgreementsAccepted,
      inputs.remember_me,
      rememberMeDays,
      is_subscribed_to_crm,
      acceptanceKey,
      autoAcceptStore,
      setState,
      setUiState,
      router,
    ],
  );

  // Callback to handle submission with date
  const handleSubmitDate = useCallback(
    (event: any) => {
      event.preventDefault();

      if (!birthDate) {
        setHasIncompleteFields(true);
        clearErrorMessage();
        return;
      }

      if (differenceInYears(new Date(), birthDate) < allowedAge) {
        setIsYounger(true);
        clearErrorMessage();
        return;
      }

      handleSubmit(event);
    },
    [birthDate, allowedAge, clearErrorMessage, handleSubmit],
  );

  // Global gate declines out of the site; a per-location one back to the store selector.
  const handleDecline = () => {
    if (!isStoreScoped) {
      window.location.href = 'about:blank';
      return;
    }

    setState({
      next_store_id: null,
      next_shop_mode: null,
      next_delivery_address: null,
    });
    setUiState({ is_store_location_open: true });
    router.replace(treez_store_name ? getSafeReturnUrl(redirectUrl) : '/');
  };

  // Callbacks for form changes
  const handleAgreementChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      genericChange(event.target.name, event.target.checked);
    },
    [genericChange],
  );

  const handleRememberMeCheck = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      genericChange(event.target.name, event.target.checked);
    },
    [genericChange],
  );

  return (
    <form
      aria-describedby="age-gate-description"
      aria-labelledby="age-gate-title"
      className={styles.age_gate__form}
      noValidate
      onSubmit={type === 'Date of Birth' ? handleSubmitDate : handleSubmit}
    >
      {type === 'Date of Birth' && (
        <fieldset>
          <legend className="sr-only">Date of Birth</legend>
          <DatePicker
            aria-describedby={
              hasIncompleteFields || isYounger
                ? 'age-gate-error-message'
                : undefined
            }
            aria-invalid={hasIncompleteFields || isYounger}
            onChange={setBirthDate}
            required
          />
        </fieldset>
      )}
      {!!rememberMeDays && (
        <fieldset>
          <legend className="sr-only">Remember me</legend>
          <Input
            checked={inputs.remember_me}
            className={styles.option}
            data-testid="remember-me-checkbox"
            id="remember_me"
            inputLabel={rememberText}
            isSmall
            name="remember_me"
            onChange={handleRememberMeCheck}
            type="checkbox"
          />
        </fieldset>
      )}
      {agreements.length > 0 && (
        <fieldset>
          <legend className="sr-only">Terms and Agreements</legend>
          {agreements.map(
            (cl, id) =>
              cl?.text && (
                <Input
                  aria-describedby={
                    !agreementsAccepted ? 'age-gate-error-message' : undefined
                  }
                  checked={inputs[`agreement_${id}`]}
                  className={styles.option}
                  id={`agreement_${id}`}
                  inputLabel={cl.text}
                  isInvalid={!agreementsAccepted}
                  isSmall
                  key={`agreement_${id}`}
                  name={`agreement_${id}`}
                  onChange={handleAgreementChange}
                  required
                  type="checkbox"
                />
              ),
          )}
        </fieldset>
      )}
      {!!rememberMeDays && agreements && agreements.length > 0 && (
        <hr aria-hidden="true" className={styles.separator} role="separator" />
      )}
      {errorMessage && (
        <div
          aria-atomic="true"
          aria-live="assertive"
          className={styles.age_gate__form_error}
          data-testid="age-gate-error-message"
          id="age-gate-error-message"
          role="alert"
        >
          <Icon aria-hidden="true" name="info" />
          {errorMessage}
        </div>
      )}
      <div className={styles.age_gate__form_actions}>
        <Button
          aria-disabled={isLoading}
          color={buttonType ?? 'primary'}
          data-testid="age-gate-submit-button"
          disabled={isLoading}
          loading={isLoading}
          onClick={() => {}}
          type="submit"
        >
          {data?.ageGate?.data.button_text}
        </Button>
        <button
          className="button button_sm button_color-link"
          onClick={handleDecline}
          type="button"
        >
          {isStoreScoped ? 'Choose another store' : 'Exit the site'}
        </button>
      </div>
    </form>
  );
};

export { AgeGate };
